import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { gbp, pct } from "../lib/format";
import { reinvest as reinvestApi } from "../lib/api";
import { getUser } from "../lib/auth";
import { Sparkles } from "lucide-react";

export const ReinvestModal = ({ open, onOpenChange, properties, unclaimedYield, onSuccess }) => {
  const [propertyId, setPropertyId] = useState("");
  const [amount, setAmount] = useState(5000);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setPropertyId(properties?.[0]?.id || "");
      setAmount(Math.max(5000, Math.floor(unclaimedYield || 0)));
    }
  }, [open, properties, unclaimedYield]);

  const selected = useMemo(() => properties?.find((p) => p.id === propertyId), [properties, propertyId]);
  const min = selected?.min_investment_gbp ?? 5000;

  const handleConfirm = async () => {
    const user = getUser();
    if (!user) return;
    if (!propertyId) return toast.error("Please choose a property.");
    if (amount < min) return toast.error(`Minimum reinvest is ${gbp(min)}.`);
    if (amount > unclaimedYield + 0.01) return toast.error(`Only ${gbp(unclaimedYield, { decimals: 2 })} unclaimed yield available.`);
    setLoading(true);
    try {
      await reinvestApi(user.id, propertyId, amount);
      toast.success(`Reinvested ${gbp(amount)} into ${selected.name}.`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reinvest failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-slate-200 bg-white p-8" data-testid="reinvest-modal">
        <DialogHeader>
          <div className="overline">Reinvest Yield</div>
          <DialogTitle className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-900">
            Compound your returns
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Allocate accrued yield into another income-producing UK asset.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-5">
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Sparkles className="h-4 w-4 brickstok-gold" /> Unclaimed yield
            </div>
            <div className="font-display text-lg font-semibold text-slate-900 font-mono-num" data-testid="reinvest-unclaimed-value">
              {gbp(unclaimedYield, { decimals: 2 })}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700">Choose property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="mt-2 h-11" data-testid="reinvest-property-select">
                <SelectValue placeholder="Select a property" />
              </SelectTrigger>
              <SelectContent>
                {properties?.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`reinvest-property-option-${p.id}`}>
                    {p.name} · {p.city} · {pct(p.projected_yield_pct)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="reinvest-amount" className="text-sm font-medium text-slate-700">Amount</Label>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono-num">£</span>
              <Input
                id="reinvest-amount"
                type="number"
                min={min}
                max={unclaimedYield}
                step={100}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value) || 0)}
                className="h-11 pl-7 font-mono-num text-lg font-semibold text-slate-900"
                data-testid="reinvest-amount-input"
              />
            </div>
            <div className="mt-2 text-xs text-slate-500 font-mono-num">
              Minimum {gbp(min)} · Available {gbp(unclaimedYield, { decimals: 2 })}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 flex-row gap-3 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="reinvest-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !propertyId || amount < min || amount > unclaimedYield + 0.01}
            className="text-slate-900 hover:brightness-95"
            style={{ background: "#D4AF37" }}
            data-testid="reinvest-confirm-btn"
          >
            {loading ? "Reinvesting..." : `Reinvest ${gbp(amount)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReinvestModal;
